import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import "../debug-amplify-config";
import "../lib/amplify"; // Import Amplify configuration
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Trophy, Medal } from "lucide-react";
import { listLeagues, listPlayerConstructs } from "../graphql/queries";
import { getLeagueStandings } from "../lib/api/standings";
import { Link } from "react-router-dom";
import LeagueSelector from "../components/LeagueSelector";
import { getCurrentLeague } from "../lib/leagueUtils";
import { getLeagueGameLabel } from "../lib/leagueGames";

const PublicStandings: React.FC = () => {
  const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null);

  // Use the properly configured Amplify client with explicit API key auth
  const client = generateClient({
    authMode: "apiKey",
  });

  // Fetch all leagues
  const { data: leagues, isLoading: leaguesLoading } = useQuery({
    queryKey: ["publicLeagues"],
    queryFn: async () => {
      try {
        console.log("PublicStandings: Starting leagues query...");
        const result = await client.graphql({
          query: listLeagues,
          variables: { limit: 100 },
        });
        console.log(
          "PublicStandings: Fetched leagues:",
          result.data.listLeagues.items
        );
        return result.data.listLeagues.items;
      } catch (error) {
        console.error("PublicStandings: Leagues query error:", error);
        throw error;
      }
    },
  });

  // Set default league when leagues are loaded
  useEffect(() => {
    if (leagues && leagues.length > 0 && !selectedLeagueId) {
      // Use date-based logic to determine the current league
      const currentLeague = getCurrentLeague(leagues as any);
      setSelectedLeagueId(currentLeague?.id || leagues[0].id);
    }
  }, [leagues, selectedLeagueId]);

  // Fetch all player constructs
  const {
    data: allPlayerConstructs,
    isLoading: playerConstructsLoading,
    error: playerConstructsError,
  } = useQuery({
    queryKey: ["publicPlayerConstructs"],
    queryFn: async () => {
      try {
        console.log("PublicStandings: Starting PlayerConstructs query...");
        const result = await client.graphql({
          query: listPlayerConstructs,
          variables: { limit: 1000 },
        });
        console.log(
          "PublicStandings: Fetched PlayerConstructs:",
          result.data.listPlayerConstructs.items
        );
        return result.data.listPlayerConstructs.items;
      } catch (error) {
        console.error("PublicStandings: PlayerConstructs query error:", error);
        throw error;
      }
    },
  });

  // Fetch stored league standings (single source of truth)
  const {
    data: storedStandings,
    isLoading: storedStandingsLoading,
    error: storedStandingsError,
  } = useQuery({
    queryKey: ["publicStoredStandings", selectedLeagueId],
    queryFn: async () => {
      try {
        console.log("PublicStandings: Fetching stored standings...");
        if (!selectedLeagueId) {
          console.log("PublicStandings: No league selected");
          return [];
        }

        console.log("PublicStandings: Using league:", selectedLeagueId);
        const standings = await getLeagueStandings(selectedLeagueId);
        console.log("PublicStandings: Fetched stored standings:", standings);
        return standings;
      } catch (error) {
        console.error(
          "PublicStandings: Error fetching stored standings:",
          error
        );
        throw error;
      }
    },
    enabled: !!selectedLeagueId, // Only run when a league is selected
  });

  // Use stored standings data (single source of truth)
  const playersWithStats = React.useMemo(() => {
    console.log("PublicStandings: Data check:", {
      storedStandings: storedStandings?.length || 0,
      allPlayerConstructs: allPlayerConstructs?.length || 0,
      storedStandingsError: storedStandingsError,
      playerConstructsError: playerConstructsError,
    });

    if (!storedStandings || storedStandings.length === 0) {
      console.log(
        "PublicStandings: No stored standings data, returning empty array"
      );
      return [];
    }

    if (!allPlayerConstructs) {
      console.log(
        "PublicStandings: No PlayerConstructs data, returning empty array"
      );
      return [];
    }

    console.log("PublicStandings: Processing stored standings data...");

    // Map stored standings to display format
    return storedStandings
      .map((standing: any) => {
        const playerConstruct = allPlayerConstructs.find(
          (pc: any) => pc.id === standing.playerConstructId
        );

        if (!playerConstruct) {
          console.warn(
            `PlayerConstruct not found for ID: ${standing.playerConstructId}`
          );
          return null;
        }

        const totalMatches =
          standing.totalWins + standing.totalLosses + standing.totalDraws;
        const matchWinPercentage =
          totalMatches > 0 ? (standing.totalWins / totalMatches) * 100 : 0;

        return {
          id: playerConstruct.id,
          displayName: playerConstruct.name,
          totalEvents: standing.totalEvents,
          totalPoints: standing.totalPoints,
          bestRank: standing.bestRank,
          averageRank: standing.averageRank,
          totalWins: standing.totalWins,
          totalLosses: standing.totalLosses,
          totalDraws: standing.totalDraws,
          totalMatches,
          matchWinPercentage,
          hasDetailedStats: totalMatches > 0,
          currentRank: standing.currentRank,
        };
      })
      .filter((player: any) => player !== null) // Remove null entries
      .sort((a: any, b: any) => {
        // Sort by current rank (ascending)
        return a.currentRank - b.currentRank;
      });
  }, [
    storedStandings,
    allPlayerConstructs,
    storedStandingsError,
    playerConstructsError,
  ]);

  const isLoading =
    leaguesLoading || playerConstructsLoading || storedStandingsLoading;

  // Get the currently selected league
  const selectedLeague = leagues?.find(
    (league: any) => league.id === selectedLeagueId
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.28em] text-primary">
            The Booster Box
          </p>
          <h1 className="mb-2 text-4xl font-bold tracking-tight text-white">
            SOVA League Hub
          </h1>
          <p className="text-muted-foreground text-lg">
            Public League Standings
          </p>
        </div>

        {selectedLeague && (
          <div className="mb-5 flex justify-center">
            <span className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              {getLeagueGameLabel(selectedLeague.game)} · {selectedLeague.format}
            </span>
          </div>
        )}

        {/* League Selector */}
        <div className="mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Select League
              </CardTitle>
              <CardDescription>
                Choose a league to view its standings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LeagueSelector
                selectedLeagueId={selectedLeagueId}
                onLeagueChange={setSelectedLeagueId}
                className="max-w-md"
              />
            </CardContent>
          </Card>
        </div>

        {/* Login CTA */}
        <Card className="mb-8 bg-primary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-primary mb-2">
                Want to see detailed stats?
              </h2>
              <p className="text-muted-foreground mb-4">
                Login to access your personal dashboard, event history, and
                detailed performance analytics.
              </p>
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium"
              >
                Login to App
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Standings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Current Standings
            </CardTitle>
            <CardDescription>
              Player rankings based on tournament performance
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading standings...</p>
              </div>
            ) : playersWithStats.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {storedStandingsError || playerConstructsError
                    ? "API Temporarily Unavailable"
                    : "No Standings Available Yet"}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {storedStandingsError || playerConstructsError ? (
                    <>
                      We're experiencing technical difficulties with our data
                      service. Please try again later or contact support if the
                      issue persists.
                    </>
                  ) : (
                    <>
                      Standings will appear here once players participate in
                      events and data is processed.
                    </>
                  )}
                </p>
                {!storedStandingsError && !playerConstructsError && (
                  <div className="text-sm text-muted-foreground mb-4">
                    <p>This could mean:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>No events have been completed yet</li>
                      <li>Standings are being calculated</li>
                      <li>No players have registered for events</li>
                    </ul>
                  </div>
                )}
                <Link
                  to="/"
                  className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  Login to App
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Rank</th>
                      <th className="text-left py-3 px-4 font-medium">
                        Player
                      </th>
                      <th className="text-center py-3 px-4 font-medium">
                        Total Points
                      </th>
                      <th className="text-center py-3 px-4 font-medium">
                        Events
                      </th>
                      <th className="text-center py-3 px-4 font-medium">
                        Best Rank
                      </th>
                      <th className="text-center py-3 px-4 font-medium">
                        Avg Rank
                      </th>
                      <th className="text-center py-3 px-4 font-medium">
                        Match Record
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {playersWithStats.map((player: any) => (
                      <tr
                        key={player.id}
                        className="border-b hover:bg-muted/50"
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {player.currentRank === 1 && (
                              <Medal className="h-4 w-4 text-yellow-500" />
                            )}
                            {player.currentRank === 2 && (
                              <Medal className="h-4 w-4 text-gray-400" />
                            )}
                            {player.currentRank === 3 && (
                              <Medal className="h-4 w-4 text-amber-600" />
                            )}
                            <span className="font-medium">
                              #{player.currentRank}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-medium">
                            {player.displayName}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center font-medium">
                          {player.totalPoints}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {player.totalEvents}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {player.bestRank ? `#${player.bestRank}` : "-"}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {player.averageRank
                            ? player.averageRank.toFixed(1)
                            : "-"}
                        </td>
                        <td className="py-3 px-4 text-center text-sm">
                          {player.hasDetailedStats ? (
                            <div>
                              <div>
                                {player.totalWins}-{player.totalLosses}-
                                {player.totalDraws}
                              </div>
                              <div className="text-muted-foreground">
                                {player.matchWinPercentage.toFixed(1)}%
                              </div>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-muted-foreground">
          <p>
            Data is calculated from authenticated league standings and updated
            automatically.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicStandings;
